import { ReactElement, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import { ItemsTitle } from 'app/components/shared/styled/ItemsTitle'
import SimpleTable from 'app/components/table/SimpleTable'
import {
  OPS_USAGE_COST_SMS_THREADS_QUERY,
  OPS_USAGE_COST_VOICE_CALLS_QUERY,
  type UsageCostLocationRow,
  type UsageCostSmsThreadRow,
  type UsageCostVoiceCallRow,
} from 'app/components/usage-tracking/graphql'
import type { FilterableColumn } from 'app/filter.utils'
import { closeModal, showModal } from 'app/lib/modal'
import {
  getUsageDetailFilterableColumns,
  getUsageSmsThreadColumns,
  getUsageVoiceCallColumns,
  readUsageDetailProduct,
  type UsageDetailProduct,
} from 'app/table-columns/analytics/usage-tracking'

export type LocationUsageDetailModalProps = {
  location: UsageCostLocationRow
  fromDate: string
  toDate: string
  onClose: () => void
}

// The location row totals come from aggregations over thousands of records; the modal
// lists the biggest contributors rather than every one of them.
const PAGE_LIMIT = 100

// opsUsageCost* live on the ops schema (/graphql/ops). Stable context object so
// the urql queries don't re-run each render.
const OPS_CONTEXT = { url: OPS_URL }

function formatCount(value: number): string {
  return value.toLocaleString(`en-US`)
}

// Voice and SMS rows have no shared shape, so each product gets its own table component
// rather than widening SimpleTable's row type to a union.
function VoiceCallsTable({
  rows,
  filterableColumns,
  onFiltersChange,
}: {
  rows: UsageCostVoiceCallRow[] | undefined
  filterableColumns: FilterableColumn[]
  onFiltersChange: (filters: string) => void
}): ReactElement {
  const columns = useMemo(() => getUsageVoiceCallColumns(), [])
  return (
    <SimpleTable
      activeSubpage="Usage Voice Calls"
      data={rows}
      columns={columns}
      filterableColumns={filterableColumns}
      onFiltersChange={onFiltersChange}
      showColumnOrdering
      enableExport
      noDataMessage="No AI agent calls in this date range."
    />
  )
}

function SmsThreadsTable({
  rows,
  filterableColumns,
  onFiltersChange,
}: {
  rows: UsageCostSmsThreadRow[] | undefined
  filterableColumns: FilterableColumn[]
  onFiltersChange: (filters: string) => void
}): ReactElement {
  const columns = useMemo(() => getUsageSmsThreadColumns(), [])
  return (
    <SimpleTable
      activeSubpage="Usage SMS Threads"
      data={rows}
      columns={columns}
      filterableColumns={filterableColumns}
      onFiltersChange={onFiltersChange}
      showColumnOrdering
      enableExport
      noDataMessage="No SMS agent conversations in this date range."
    />
  )
}

export function LocationUsageDetailModal({
  location,
  fromDate,
  toDate,
  onClose,
}: LocationUsageDetailModalProps): ReactElement {
  // Open on whichever product actually drove this row's cost; the Product filter takes
  // over from there.
  const [product, setProduct] = useState<UsageDetailProduct>(
    location.smsCostUsd > location.voiceCostUsd ? `sms` : `voice`,
  )

  const input = {
    locationId: location.locationId,
    dateRange: { fromDate, toDate },
    pagination: { page: 0, limit: PAGE_LIMIT },
  }

  const [voiceResult] = useQuery({
    query: OPS_USAGE_COST_VOICE_CALLS_QUERY,
    context: OPS_CONTEXT,
    variables: { input, sort: { field: `totalCostUsd`, direction: `DESC` } },
    pause: product !== `voice`,
  })
  const [smsResult] = useQuery({
    query: OPS_USAGE_COST_SMS_THREADS_QUERY,
    context: OPS_CONTEXT,
    variables: { input },
    pause: product !== `sms`,
  })

  const filterableColumns = useMemo(() => getUsageDetailFilterableColumns(), [])
  const onFiltersChange = (filters: string) => setProduct(readUsageDetailProduct(filters))

  const isVoice = product === `voice`
  const active = isVoice ? voiceResult : smsResult
  const result = isVoice ? voiceResult.data?.opsUsageCostVoiceCalls : smsResult.data?.opsUsageCostSmsThreads
  const isLoading = active.fetching && !active.data
  const shown = result?.hits.length ?? 0
  const total = result?.total ?? 0

  return (
    <div x-component="LocationUsageDetailModal" className="flex h-[100vh] max-h-[100vh] flex-col overflow-hidden px-5">
      <ItemsTitle
        title={location.locationName ?? `Unknown Location`}
        subtitle={location.workspaceName ?? undefined}
        items={total}
        onClose={onClose}
      />

      {active.error ? (
        <p className="py-10 text-center text-body-sm text-subtle">{active.error.message}</p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {isVoice ? (
            <VoiceCallsTable
              rows={isLoading ? undefined : [...(voiceResult.data?.opsUsageCostVoiceCalls.hits ?? [])]}
              filterableColumns={filterableColumns}
              onFiltersChange={onFiltersChange}
            />
          ) : (
            <SmsThreadsTable
              rows={isLoading ? undefined : [...(smsResult.data?.opsUsageCostSmsThreads.hits ?? [])]}
              filterableColumns={filterableColumns}
              onFiltersChange={onFiltersChange}
            />
          )}
          {/* The record lists are capped, so say so rather than letting a truncated list
              read as "these rows are the whole total". */}
          {shown < total && (
            <p className="shrink-0 pt-2 text-body-xs text-subtle">
              Showing the {formatCount(shown)} most expensive of {formatCount(total)}{' '}
              {isVoice ? `calls` : `conversations`}.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Open the drill-down as a full-screen modal. Shared by the Location Name cell and the
 * `?locationDetail=` deep link so both land on the same modal options.
 */
export function openLocationUsageDetail(location: UsageCostLocationRow, fromDate: string, toDate: string): void {
  showModal(
    <LocationUsageDetailModal location={location} fromDate={fromDate} toDate={toDate} onClose={() => closeModal()} />,
    { hideTitle: true, removePadding: true, isFullScreen: true, useRadix: true },
  )
}
